<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import moment from "moment";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  manufacturerList,
  manufacturerAdd,
  manufacturerDel,
} from "@/common/api/rest/manufacturer";

import type {
  ManufacturerType,
  ManufacturerPayloadType,
} from "@/common/types/manufacturer";

import { FormManufacturer } from "@/components";
import {
  baseAvatar,
  breadcrumbSimple,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const router = useRouter();

const tableData = ref<ManufacturerType[]>([]);
const payload = ref<ManufacturerPayloadType | null>(null);

const selected = ref<ManufacturerType | null>(null);

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);

const tableHeader = computed(() => [
  { key: "avatar", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
  { key: "created", label: t("common.labels.created") },
  { key: "updated", label: t("common.labels.updated") },
]);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "manufacturer",
    label: t("common.labels.manufacturers"),
    url: `/register/manufacturer`,
  },
]);

const status = computed(() => ({
  ACTIVE: t("common.labels.active"),
  INACTIVE: t("common.labels.inactive"),
}));

function fetchData() {
  manufacturerList({
    page: page.value,
    limit: limit.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function searchItem(value: string) {
  search.value = value;

  fetchData();
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  manufacturerAdd(payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        editItem(data);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(data: ManufacturerType) {
  router.push(`/register/manufacturer/${data.manufacturer_id}/edit`);
}

function removeItem(data: ManufacturerType) {
  selected.value = data;
  showModalDel.value = true;
}

function removeItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function removeItemConfirm() {
  if (!selected.value) return;

  manufacturerDel(selected.value.manufacturer_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="manufacturer"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <svg-icon icon="no-image" size="2rem" />
        {{ t("common.labels.manufacturers") }}
      </div>

      <div class="tab-content">
        <base-table
          :header="tableHeader"
          :data="tableData"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :del="true"
          :edit="true"
          @change:page="changePage($event)"
          @searching="searchItem($event)"
          @btn:add="addItem()"
          @btn:edit="editItem($event)"
          @btn:del="removeItem($event)"
        >
          <template #avatar="{ data }">
            <base-avatar
              position="center"
              :is-edit="false"
              :show-image="true"
              :url="data?.avatar?.large"
              :aspect-ratio="1.333"
              size="3rem"
              border-radius="0.25rem"
              background="#fff"
            />
          </template>

          <template #status="{ data }">
            {{ status[data.status]?.toUpperCase() }}
          </template>

          <template #created="{ data }">
            {{ moment(data.created).format("DD/MM/YYYY HH:mm:ss") }}
          </template>

          <template #updated="{ data }">
            <template v-if="data.updated">
              {{ moment(data.updated).format("DD/MM/YYYY HH:mm:ss") }}
            </template>
          </template>
        </base-table>
      </div>
    </section>
  </article>

  <base-modal
    :show="showModalAdd"
    :title="t('manufacturer.modals.titles.add_manufacturer').toUpperCase()"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="addItemCancel()"
    @confirm="addItemConfirm()"
  >
    <template #content>
      <form-manufacturer
        @update:data="payload = $event"
        @validate="payloadValid = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showModalDel"
    :title="t('manufacturer.modals.titles.del_manufacturer').toUpperCase()"
    :message="
      t('manufacturer.modals.messages.del_manufacturer', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeItemCancel()"
    @confirm="removeItemConfirm()"
  />
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: inline-flex;
      gap: 0.5rem;
      font-size: 1.2rem;
      align-items: center;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      border-block-end: 1px solid var(--border);
      text-transform: uppercase;
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      padding-inline: 1.5rem;
      padding-block: 1rem;
      gap: 1rem;
    }
  }
}
</style>
